import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'argus-appearance'
const APPEARANCE_OPTIONS = new Set(['system', 'light', 'dark'])

const AppearanceContext = createContext({
  preference: 'system',
  resolvedAppearance: 'dark',
  setPreference: () => {},
})

function systemAppearance() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function storedPreference() {
  if (typeof window === 'undefined') return 'system'
  const value = window.localStorage.getItem(STORAGE_KEY)
  return APPEARANCE_OPTIONS.has(value) ? value : 'system'
}

export function AppearanceProvider({ children }) {
  const [preference, setPreferenceState] = useState(storedPreference)
  const [systemMode, setSystemMode] = useState(systemAppearance)
  const resolvedAppearance = preference === 'system' ? systemMode : preference

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const handleChange = (event) => setSystemMode(event.matches ? 'light' : 'dark')
    media.addEventListener?.('change', handleChange)
    return () => media.removeEventListener?.('change', handleChange)
  }, [])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = resolvedAppearance
      document.documentElement.dataset.appearancePreference = preference
      document.documentElement.style.colorScheme = resolvedAppearance
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, preference)
    }
  }, [preference, resolvedAppearance])

  function setPreference(nextPreference) {
    if (!APPEARANCE_OPTIONS.has(nextPreference)) return
    setPreferenceState(nextPreference)
  }

  const value = useMemo(
    () => ({ preference, resolvedAppearance, setPreference }),
    [preference, resolvedAppearance],
  )

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>
}

export function useAppearance() {
  return useContext(AppearanceContext)
}

export const ARGUS_APPEARANCE_OPTIONS = Object.freeze(['system', 'light', 'dark'])
